import { AnimatePresence, motion } from 'framer-motion';
import { useRef, useEffect, MutableRefObject } from 'react';

import SpinnerStroked from 'components/SpinnerStroked';
import Box from 'components/base/Box';
import Flex from 'components/base/Flex';

interface IntersectionObserverProps {
  target: MutableRefObject<HTMLDivElement>;
  onIntersect: () => void;
  enabled: boolean;
  rootMargin?: string;
  root?: MutableRefObject<HTMLDivElement>;
  threshold?: number;
}

function useIntersectionObserver({
  root,
  target,
  onIntersect,
  threshold = 1.0,
  rootMargin = '0px',
  enabled = true,
}: IntersectionObserverProps): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onIntersect();
          }
        }),
      {
        root: root?.current,
        rootMargin,
        threshold,
      }
    );

    const el = target && target.current;

    if (!el) {
      return;
    }

    observer.observe(el);

    return () => {
      observer.unobserve(el);
    };
  }, [target.current, enabled]);
}

interface InfiniteScrollButtonProps {
  handleNextPage: () => void;
  hasNextPage?: boolean;
  isFetching: boolean;
  enabled?: boolean;
  animationDuration?: number;
}

export default function InfiniteScrollButton(props: InfiniteScrollButtonProps) {
  const {
    handleNextPage,
    isFetching,
    enabled = true,
    hasNextPage = false,
    animationDuration = 0.1,
  } = props;

  const loadMoreButtonRef = useRef<HTMLDivElement>(null);

  useIntersectionObserver({
    target: loadMoreButtonRef as MutableRefObject<HTMLDivElement>,
    onIntersect: () => {
      handleNextPage();
    },
    enabled: hasNextPage && enabled,
  });

  return (
    <>
      <Box
        ref={loadMoreButtonRef}
        css={{
          height: 1,
          marginBottom: 1,
        }}
      />
      <ActivityIndicator
        isActive={isFetching && hasNextPage}
        animationDuration={animationDuration}
      />
    </>
  );
}

interface ActivityIndicatorProps
  extends Pick<InfiniteScrollButtonProps, 'animationDuration'> {
  isActive: boolean;
}

export function ActivityIndicator(props: ActivityIndicatorProps) {
  const { isActive, animationDuration = 0.1 } = props;
  return (
    <AnimatePresence exitBeforeEnter>
      {isActive && (
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 30, opacity: 0 }}
          style={{ position: 'fixed', right: 24, bottom: 24 }}
          transition={{ ease: 'easeInOut', duration: animationDuration }}
        >
          <Flex
            css={{
              padding: 8,
              backgroundColor: '$white100',
              boxShadow: '$regular1',
              borderRadius: '$round',
            }}
          >
            <SpinnerStroked size={20} />
          </Flex>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
